import { PAGE_API_QUERY_KEY_HEAD } from '@Core/clients/pageApiQueryKeys';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  ApiError,
  SdkScheduledNotificationDetail,
  SdkScheduledNotificationPreview,
  SdkScheduledNotificationEdits,
  SdkUpdateScheduledNotificationInput,
  SdkUpdateScheduledNotificationHandle,
  SdkSendScheduledNotificationNowHandle,
  SdkApiHandle,
  SdkUpcomingNotifications,
  SdkUpcomingNotificationsOptions,
  SdkScheduleNotificationInput,
  SdkScheduledNotificationResult,
  SdkCancelledNotificationResult,
  SdkScheduleNotificationHandle,
  SdkCancelScheduledNotificationHandle,
  SdkHeldNotificationResult,
  SdkHoldScheduledNotificationHandle,
  SdkResumeScheduledNotificationHandle
} from '@Pages/pageRuntime/sdk/types';
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useApi } from './useApi';
import { NOTIFICATION_HISTORY_QUERY_KEY } from './useNotificationHistory';

const URL = '/api/scheduled-notifications';
const QUERY_KEY = 'scheduled-notifications';

/** Every scheduled mutation moves an occurrence between lifecycle lists. */
function invalidateNotificationLists(queryClient: QueryClient): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: [PAGE_API_QUERY_KEY_HEAD, QUERY_KEY] }),
    queryClient.invalidateQueries({
      queryKey: [PAGE_API_QUERY_KEY_HEAD, NOTIFICATION_HISTORY_QUERY_KEY]
    })
  ]);
}

export function useUpcomingNotifications(
  options: SdkUpcomingNotificationsOptions
): SdkApiHandle<SdkUpcomingNotifications> {
  const params = {
    resource_id: options.resourceId,
    record_id: options.recordId,
    page: options.page ?? 1,
    per_page: options.perPage ?? 25
  };
  return useApi([QUERY_KEY, params], (api) => api.get<SdkUpcomingNotifications>(URL, { params }), {
    enabled: (options.enabled ?? true) && !!options.resourceId && !!options.recordId
  });
}

export function useScheduleNotification(): SdkScheduleNotificationHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    SdkScheduledNotificationResult,
    ApiError,
    SdkScheduleNotificationInput
  >({
    retry: false,
    mutationFn: async (input) => {
      try {
        const response = await axios.post<SdkScheduledNotificationResult>(URL, input);
        return response.data;
      } catch (error) {
        throw toApiError(error);
      }
    },
    onSuccess: () => invalidateNotificationLists(queryClient)
  });
  return {
    schedule: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset
  };
}

export function useCancelScheduledNotification(): SdkCancelScheduledNotificationHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    SdkCancelledNotificationResult,
    ApiError,
    { id: string; expectedRevision: number }
  >({
    retry: false,
    mutationFn: async ({ id, expectedRevision }) => {
      try {
        const response = await axios.post<SdkCancelledNotificationResult>(
          `${URL}/${encodeURIComponent(id)}/cancel`,
          { expected_revision: expectedRevision }
        );
        return response.data;
      } catch (error) {
        throw toApiError(error);
      }
    },
    onSuccess: () => invalidateNotificationLists(queryClient)
  });
  return {
    cancel: (id, expectedRevision) => mutation.mutateAsync({ id, expectedRevision }),
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset
  };
}

export function useScheduledNotification(
  scheduledNotificationId: string | null | undefined
): SdkApiHandle<SdkScheduledNotificationDetail> {
  return useApi(
    [QUERY_KEY, 'detail', scheduledNotificationId],
    (api) =>
      api.get<SdkScheduledNotificationDetail>(
        `${URL}/${encodeURIComponent(scheduledNotificationId ?? '')}`
      ),
    { enabled: !!scheduledNotificationId }
  );
}

export function usePreviewScheduledNotification(
  scheduledNotificationId: string | null | undefined
): SdkApiHandle<SdkScheduledNotificationPreview> {
  return useApi(
    [QUERY_KEY, 'preview', scheduledNotificationId],
    async () => {
      const response = await axios.post<SdkScheduledNotificationPreview>(
        `${URL}/${encodeURIComponent(scheduledNotificationId ?? '')}/preview`,
        { render_values: true }
      );
      return response.data;
    },
    { enabled: !!scheduledNotificationId }
  );
}

export function useUpdateScheduledNotification(): SdkUpdateScheduledNotificationHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    SdkScheduledNotificationResult,
    ApiError,
    { id: string; input: SdkUpdateScheduledNotificationInput }
  >({
    retry: false,
    mutationFn: async ({ id, input }) => {
      try {
        const response = await axios.put<SdkScheduledNotificationResult>(
          `${URL}/${encodeURIComponent(id)}`,
          input
        );
        return response.data;
      } catch (error) {
        throw toApiError(error);
      }
    },
    onSuccess: () => invalidateNotificationLists(queryClient)
  });
  return {
    update: (id, input) => mutation.mutateAsync({ id, input }),
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset
  };
}

export function useSendScheduledNotificationNow(): SdkSendScheduledNotificationNowHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    { id: string; status: 'queued' },
    ApiError,
    { id: string; input: SdkScheduledNotificationEdits }
  >({
    retry: false,
    mutationFn: async ({ id, input }) => {
      try {
        const response = await axios.post<{ id: string; status: 'queued' }>(
          `${URL}/${encodeURIComponent(id)}/send-now`,
          input
        );
        return response.data;
      } catch (error) {
        throw toApiError(error);
      }
    },
    onSuccess: () =>
      Promise.all([
        invalidateNotificationLists(queryClient),
        queryClient.invalidateQueries({ queryKey: ['email-messages', 'search'] })
      ])
  });
  return {
    sendNow: (id, input) => mutation.mutateAsync({ id, input }),
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset
  };
}

export function useHoldScheduledNotification(): SdkHoldScheduledNotificationHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    SdkHeldNotificationResult,
    ApiError,
    { id: string; expectedRevision: number }
  >({
    retry: false,
    mutationFn: async ({ id, expectedRevision }) => {
      try {
        const response = await axios.post<SdkHeldNotificationResult>(
          `${URL}/${encodeURIComponent(id)}/hold`,
          { expected_revision: expectedRevision }
        );
        return response.data;
      } catch (error) {
        throw toApiError(error);
      }
    },
    onSuccess: () => invalidateNotificationLists(queryClient)
  });
  return {
    hold: (id, expectedRevision) => mutation.mutateAsync({ id, expectedRevision }),
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset
  };
}

export function useResumeScheduledNotification(): SdkResumeScheduledNotificationHandle {
  const queryClient = useQueryClient();
  const mutation = useMutation<
    SdkHeldNotificationResult,
    ApiError,
    { id: string; expectedRevision: number; scheduledAt?: string }
  >({
    retry: false,
    mutationFn: async ({ id, expectedRevision, scheduledAt }) => {
      const payload = {
        expected_revision: expectedRevision,
        ...(scheduledAt === undefined ? {} : { scheduled_at: scheduledAt })
      };
      try {
        const response = await axios.post<SdkHeldNotificationResult>(
          `${URL}/${encodeURIComponent(id)}/resume`,
          payload
        );
        return response.data;
      } catch (error) {
        throw toApiError(error);
      }
    },
    onSuccess: () => invalidateNotificationLists(queryClient)
  });
  return {
    resume: (id, expectedRevision, scheduledAt) =>
      mutation.mutateAsync({ id, expectedRevision, scheduledAt }),
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset
  };
}
